import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/stores/authStore";

type AuthView = "enter-email" | "link-sent";
type LoginMode = "link" | "password";

export function LoginPage() {
  const { t } = useTranslation();
  const { requestMagicLink, loginWithPassword, isLoading, error, linkSent, linkEmail } = useAuthStore();

  const [view, setView] = useState<AuthView>("enter-email");
  const [mode, setMode] = useState<LoginMode>("link");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startResendCountdown = useCallback(() => {
    setResendCountdown(30);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setResendCountdown((n) => {
        if (n <= 1) {
          clearInterval(countdownRef.current!);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
  }, []);

  // Sync linkSent from store → view
  useEffect(() => {
    if (linkSent) {
      setView("link-sent");
      startResendCountdown();
    }
  }, [linkSent, startResendCountdown]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    useAuthStore.setState({ error: null });
    if (mode === "password") {
      await loginWithPassword(email, password);
    } else {
      await requestMagicLink(email);
    }
  }

  async function handleResend() {
    if (resendCountdown > 0) return;
    useAuthStore.setState({ error: null });
    await requestMagicLink(linkEmail ?? email);
    startResendCountdown();
  }

  function handleDifferentEmail() {
    useAuthStore.setState({ linkSent: false, linkEmail: null, error: null });
    setView("enter-email");
    setEmail("");
    if (countdownRef.current) clearInterval(countdownRef.current);
    setResendCountdown(0);
  }

  const displayError = error ? t(`auth.${error}`, { defaultValue: t("auth.requestFailed") }) : "";

  return (
    <div className="cx-login-bg flex h-full items-center justify-center">

      {view === "enter-email" && (
        <form onSubmit={handleSubmit} className="cx-login-card w-full max-w-sm p-8">
          <h1 className="cx-login-heading mb-1 text-center text-2xl font-bold">Ortvis</h1>
          <p className="cx-login-subtitle mb-6 text-center text-[13px]">
            {t("common.tagline")}
          </p>

          {displayError && (
            <div className="cx-login-error mb-4 rounded-lg p-3 text-[13px]">{displayError}</div>
          )}

          <label className="mb-4 block">
            <span className="cx-login-label mb-1.5 block text-[13px] font-medium">
              {t("auth.email")}
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              className="cx-login-input w-full px-3 py-2.5 text-[15px] focus:outline-none"
            />
          </label>

          {mode === "password" && (
            <label className="mb-4 block">
              <span className="cx-login-label mb-1.5 block text-[13px] font-medium">
                {t("auth.password")}
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="cx-login-input w-full px-3 py-2.5 text-[15px] focus:outline-none"
              />
            </label>
          )}

          {mode === "link" && (
            <p className="mb-6 text-[12px] cx-text-secondary">{t("auth.linkSubtext")}</p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="cx-login-submit w-full py-2.5 text-[14px] font-semibold transition-all duration-150 active:scale-[0.98] disabled:opacity-50"
          >
            {isLoading ? t("auth.loading") : mode === "password" ? t("auth.login") : t("auth.sendMeALink")}
          </button>

          <button
            type="button"
            onClick={() => { setMode(mode === "link" ? "password" : "link"); useAuthStore.setState({ error: null }); }}
            className="cx-text-accent text-[12px] mt-4 block mx-auto"
          >
            {mode === "link" ? t("auth.usePassword", "Log in with password") : t("auth.useLink", "Log in with link")}
          </button>
        </form>
      )}

      {view === "link-sent" && (
        <div className="cx-login-card w-full max-w-sm p-8 text-center">
          <h1 className="cx-login-heading mb-1 text-center text-2xl font-bold">Ortvis</h1>
          <p className="cx-login-subtitle mb-6 text-center text-[13px]">
            {t("auth.linkSentHeading")}
          </p>

          <p className="mb-6 text-[14px] cx-text-primary leading-relaxed">
            {t("auth.linkSentBody", { email: linkEmail ?? email })}
          </p>

          <button
            type="button"
            onClick={handleResend}
            disabled={resendCountdown > 0 || isLoading}
            className="cx-login-submit w-full py-2.5 text-[14px] font-semibold transition-all duration-150 active:scale-[0.98] disabled:opacity-50 mb-3"
          >
            {resendCountdown > 0
              ? t("auth.resendIn", { seconds: resendCountdown })
              : t("auth.resendLink")}
          </button>

          <button
            type="button"
            onClick={handleDifferentEmail}
            className="cx-text-accent text-[13px]"
          >
            {t("auth.useDifferentEmail")}
          </button>
        </div>
      )}
    </div>
  );
}
